import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

/**
 * Gallery — service detay sayfasında "Saha Görselleri" bölümü.
 * /gallery.php?service=<key> endpoint'inden JSON çeker.
 * Boş gelirse hiçbir şey render etmez (boş başlık/kart bırakmaz).
 */
export default function Gallery({ serviceKey }) {
  const [items, setItems] = useState(null); // null = loading, [] = empty, [...] = data
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!serviceKey) {
      setItems([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/gallery.php?service=${encodeURIComponent(serviceKey)}`, {
          headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error("not ok");
        const data = await res.json();
        if (cancelled) return;
        setItems(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [serviceKey]);

  // Loading: skeleton yerine sessizce bekle (boş alan oluşturmamak için).
  if (items === null) {
    return (
      <div className="py-10 text-center text-slate-400" aria-busy="true">
        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
      </div>
    );
  }

  // Boş galeri → hiçbir şey gösterme
  if (items.length === 0) return null;

  return (
    <section
      data-testid="gallery-section"
      className="bg-white border-t border-slate-200 py-16 lg:py-20"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="overline mb-3">SAHA GÖRSELLERİ</div>
        <h2
          className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-10"
          style={{ fontFamily: "Outfit" }}
        >
          Uygulama görselleri
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((it, i) => (
            <button
              key={`${it.src}-${i}`}
              type="button"
              onClick={() => setLightbox(it)}
              data-testid={`gallery-item-${i}`}
              className="ind-card overflow-hidden text-left card-lift focus:outline-none focus:ring-2 focus:ring-cyan-600/40"
            >
              <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                <img
                  src={it.src}
                  alt={it.title || ""}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-[1.03]"
                />
              </div>
              {(it.title || it.description) && (
                <div className="p-5">
                  {it.title && (
                    <h3
                      className="text-base font-bold text-slate-900 mb-1 line-clamp-2"
                      style={{ fontFamily: "Outfit" }}
                    >
                      {it.title}
                    </h3>
                  )}
                  {it.description && (
                    <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">
                      {it.description}
                    </p>
                  )}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[60] bg-slate-900/90 backdrop-blur-sm flex items-center justify-center p-4 cursor-zoom-out"
        >
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightbox.src}
              alt={lightbox.title || ""}
              className="w-full max-h-[80vh] object-contain rounded-sm"
            />
            {(lightbox.title || lightbox.description) && (
              <div className="mt-4 text-white">
                {lightbox.title && (
                  <h4 className="text-lg font-semibold" style={{ fontFamily: "Outfit" }}>
                    {lightbox.title}
                  </h4>
                )}
                {lightbox.description && (
                  <p className="text-sm text-slate-300 mt-1">{lightbox.description}</p>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={() => setLightbox(null)}
              className="mt-4 btn-secondary text-sm bg-white"
            >
              Kapat
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
